import React from 'react';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { setPdfTimeSetting, getLocalTime } from '../time';
import { TemplateDocument } from './templates';
import { AuditDocument } from './audits/audits';
import { ExecutiveSummaryDocument } from './audits/executive_summary';
import { IncidentDocument } from './incident';
import { AssessmentDocument } from './assessment_components/assessment';
import CertificatePDF from './certificate';
import { ReportDocument } from './report';
import { CorrectiveActionDocument } from './audits/corrective_actions';
import { ObservationDocument } from './observation/observation';
import i18n from '../../i18n';
import {
  buildCustomReportDocument,
  formatTimeZoneName,
} from '@opas-mobile/report-export';

// Browser's own zone, shown on the report so it's explicit rather than
// implied — same helper the backend uses for the emailed version, so a
// report looks the same regardless of which path generated it
function browserTimeZoneLabel() {
  try {
    return formatTimeZoneName(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

export const downloadPDF = async ({
  inspectionTemplate = null,
  audit = null,
  props = {},
  summary = null,
  company = {},
  correctiveActions = [],
  correctiveAction = null,
  notes = [],
  showDetails,
  fileName,
  incident = null,
  incidentProps = [],
  inspections = [],
  actionItems = [],
  inspection = null,
  certificate = null,
  observation = null,
  report = null,
  customReport = null,
  lookups,
  timeSetting = 'standard',
}) => {
  setPdfTimeSetting(timeSetting);
  const getDocument = async () => {
    if (!!incident) {
      return (
        <IncidentDocument
          incident={incident}
          inspections={inspections || []}
          incidentImages={incident?.incidentImages || []}
          workspace={incident?.workspace}
          notes={incident?.notes || []}
          fileName={fileName}
          props={incidentProps}
          actionItems={actionItems}
        />
      );
    } else if (!!inspectionTemplate) {
      return <TemplateDocument inspectionTemplate={inspectionTemplate} />;
    } else if (!!audit) {
      return (
        <AuditDocument
          inspection={audit}
          props={props}
          correctiveActions={correctiveActions}
          notes={notes}
        />
      );
    } else if (!!summary) {
      return (
        <ExecutiveSummaryDocument
          inspection={summary}
          company={company}
          correctiveActions={correctiveActions}
          showDetails={showDetails}
        />
      );
    } else if (!!inspection) {
      return (
        <AssessmentDocument
          inspection={inspection}
          workspace={inspection.workspace}
          actionItems={actionItems || []}
          notes={notes}
          props={props}
        />
      );
    } else if (!!certificate) {
      return (
        <CertificatePDF certificate={certificate} logo={company.logoUrl} />
      );
    } else if (!!report) {
      return <ReportDocument report={report} props={props} company={company} />;
    } else if (!!customReport) {
      return buildCustomReportDocument({
        customReport,
        company,
        props,
        lookups,
        t: i18n.t,
        getTime: getLocalTime,
        timeSetting,
        locale: i18n.resolvedLanguage,
        timeZoneLabel: browserTimeZoneLabel(),
        assetBaseUrl: window.location.origin,
      });
    } else if (!!correctiveAction) {
      return (
        <CorrectiveActionDocument
          correctiveAction={correctiveAction}
          company={company}
        />
      );
    } else if (!!observation) {
      return (
        <ObservationDocument
          observation={observation}
          company={company}
          props={props}
          notes={notes}
        />
      );
    } else {
      return null;
    }
  };
  const document = await getDocument();
  if (!document) return;
  try {
    const blob = await pdf(document)?.toBlob();
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `${fileName || 'Document'}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    return blob;
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    return null;
  }
};

export const devPdfViewer = ({ document }) => (
  <PDFViewer
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      height: '80vh',
      width: '80vw',
      zIndex: 1000,
    }}
  >
    {document}
  </PDFViewer>
);
